import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import AIAssistant from './AIAssistant';
import CreateAdmin from './CreateAdmin';
import LiveMap from './LiveMap';
import Settings from './Settings';

const API = 'https://weridesudan.com/api/admin';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatTs = (ts?: string | null) =>
  ts ? new Date(ts).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  }) : '–';

const starRating = (val: number | null | undefined) =>
  val != null ? `⭐ ${Number(val).toFixed(1)}` : '—';

function AvatarCell({ name, photoUrl }: { name: string; photoUrl?: string }) {
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const hue = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360;
  const circle: React.CSSProperties = { width: 34, height: 34, borderRadius: '50%', flexShrink: 0, border: '2px solid #e8edf2' };
  if (photoUrl) {
    return <img src={photoUrl} alt={initials} style={{ ...circle, objectFit: 'cover', display: 'block' }} />;
  }
  return (
    <div style={{ ...circle, background: `hsl(${hue},45%,58%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 600 }}>
      {initials}
    </div>
  );
}

// ─── Chart helpers ────────────────────────────────────────────────────────────

const PALETTE = ['#e63946','#f4a261','#f9c74f','#43aa8b','#4361ee','#7209b7','#3a86ff','#06d6a0'];

const STATUS_COLORS: Record<string, string> = {
  completed:   '#22c55e',
  cancelled:   '#ef4444',
  searching:   '#f59e0b',
  accepted:    '#4361ee',
  arriving:    '#7209b7',
  in_progress: '#06d6a0',
};

function fillDates(data: { _id: string; [k: string]: any }[], days: number, valueKey: string) {
  const map: Record<string, number> = {};
  data.forEach(d => { map[d._id] = d[valueKey] ?? 0; });
  const out: { date: string; [k: string]: any }[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    out.push({ date: key, [valueKey]: map[key] ?? 0 });
  }
  return out;
}

const shortDate = (s: string) => {
  const d = new Date(s + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Generate realistic sample data for a ride-hailing app
function makeSampleData(days: number) {
  const ridesPerDay: { _id: string; count: number }[] = [];
  const revenuePerDay: { _id: string; revenue: number }[] = [];
  const riderSignups: { _id: string; count: number }[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
    const rides = Math.round(8 + Math.random() * 14 + (isWeekend ? 8 : 0));
    ridesPerDay.push({ _id: key, count: rides });
    revenuePerDay.push({ _id: key, revenue: Math.round(rides * (280 + Math.random() * 120)) });
    riderSignups.push({ _id: key, count: Math.round(1 + Math.random() * 5) });
  }
  return {
    ridesPerDay,
    revenuePerDay,
    statusBreakdown: [
      { _id: 'completed', count: 42 }, { _id: 'cancelled', count: 8 },
      { _id: 'in_progress', count: 3 }, { _id: 'searching', count: 2 },
    ],
    topDrivers: [
      { name: 'Ahmed Hassan', count: 18 }, { name: 'Omar Saleh', count: 14 },
      { name: 'Khalid Nour', count: 11 }, { name: 'Yusuf Ali', count: 9 },
      { name: 'Ibrahim Musa', count: 7 },
    ],
    riderSignups,
    isSample: true,
  };
}

function ChartCard({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <div style={{ backgroundColor: '#fff', borderRadius: 18, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '24px 24px 16px' }}>
      <div style={{ marginBottom: 18 }}>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' }}>{title}</p>
        <p style={{ margin: '3px 0 0', fontSize: 12, color: '#94a3b8' }}>{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

// ─── Shared overlay ───────────────────────────────────────────────────────────

function Overlay({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modalBox} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

// ─── Confirm dialog ───────────────────────────────────────────────────────────

function ConfirmDialog({ message, onConfirm, onCancel, confirmLabel = 'Delete', confirmColor = '#e53935' }: {
  message: string; onConfirm: () => void; onCancel: () => void;
  confirmLabel?: string; confirmColor?: string;
}) {
  return (
    <Overlay onClose={onCancel}>
      <h3 style={styles.modalTitle}>Confirm</h3>
      <p style={{ color: '#444', margin: '0 0 20px' }}>{message}</p>
      <div style={styles.modalActions}>
        <button style={{ ...styles.actionBtn, backgroundColor: '#9ca3af' }} onClick={onCancel}>Cancel</button>
        <button style={{ ...styles.actionBtn, backgroundColor: confirmColor }} onClick={onConfirm}>{confirmLabel}</button>
      </div>
    </Overlay>
  );
}

// ─── Add user modal ────────────────────────────────────────────────────────────

type UserRole = 'driver' | 'rider' | 'dispatcher';

function AddUserModal({ role, token, onClose, onDone }: {
  role: UserRole; token: string; onClose: () => void; onDone: () => void;
}) {
  const isDispatcher = role === 'dispatcher';
  const [form, setForm] = useState({ name: '', phone: '', email: '', password: isDispatcher ? '' : 'WeRide123!' });
  const [err, setErr] = useState('');
  const h = { Authorization: `Bearer ${token}` };
  const label = role === 'driver' ? 'Driver' : role === 'rider' ? 'Rider' : 'Dispatcher';
  const fields: (keyof typeof form)[] = isDispatcher
    ? ['name', 'phone', 'email', 'password']
    : ['name', 'phone', 'email'];

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) { setErr('Name and phone are required.'); return; }
    if (isDispatcher && !form.password.trim()) { setErr('Password is required.'); return; }
    try {
      await axios.post(`${API}/users`, { ...form, role }, { headers: h });
      onDone(); onClose();
    } catch (e: any) {
      setErr(e.response?.data?.message || 'Failed to create user.');
    }
  };

  return (
    <Overlay onClose={onClose}>
      <h3 style={styles.modalTitle}>Add New {label}</h3>
      {fields.map(f => (
        <input key={f} type={f === 'password' ? 'password' : 'text'} style={styles.formInput}
          placeholder={f.charAt(0).toUpperCase() + f.slice(1)} value={form[f]}
          onChange={e => setForm(p => ({ ...p, [f]: e.target.value }))} />
      ))}
      {err && <p style={{ color: '#e53935', fontSize: 13, margin: '4px 0 8px' }}>{err}</p>}
      <div style={styles.modalActions}>
        <button style={{ ...styles.actionBtn, backgroundColor: '#9ca3af' }} onClick={onClose}>Cancel</button>
        <button style={{ ...styles.actionBtn, backgroundColor: '#1a73e8' }} onClick={submit}>Create {label}</button>
      </div>
    </Overlay>
  );
}

// ─── Profile modal ─────────────────────────────────────────────────────────────

function ProfileModal({ user, onClose }: { user: any; onClose: () => void }) {
  const rawStatus: string = user.status || (user.isActive ? 'active' : 'suspended');
  const statusColor: Record<string, string> = { pending: '#f59e0b', active: '#4caf50', suspended: '#f97316', rejected: '#e53935' };

  const rows: [string, React.ReactNode][] = [
    ['Role', user.role?.toUpperCase() || '–'],
    ['Phone', user.phone || '–'],
    ['Email', user.email || '–'],
    ['Status', <span style={{ ...styles.badge, backgroundColor: statusColor[rawStatus] ?? '#9ca3af' }}>
      {rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1)}
    </span>],
    ['Online', user.isOnline ? '🟢 Online' : '🔴 Offline'],
    ['Avg Rating', user.rating ? `⭐ ${Number(user.rating).toFixed(2)}` : '–'],
    ['Total Rides', user.totalRides ?? '–'],
    ['Signed Up', formatTs(user.createdAt)],
  ];

  return (
    <Overlay onClose={onClose}>
      <h3 style={styles.modalTitle}>{user.name}</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '9px 4px', color: '#888', width: 110 }}>{k}</td>
              <td style={{ padding: '9px 4px', fontWeight: 500, color: '#111' }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ textAlign: 'right', marginTop: 18 }}>
        <button style={{ ...styles.actionBtn, backgroundColor: '#1a73e8' }} onClick={onClose}>Close</button>
      </div>
    </Overlay>
  );
}

// ─── Edit user modal ───────────────────────────────────────────────────────────

function EditUserModal({ user, token, onClose, onDone }: {
  user: any; token: string; onClose: () => void; onDone: () => void;
}) {
  const [form, setForm] = useState({
    name:              user.name || '',
    phone:             user.phone || '',
    email:             user.email || '',
    vehicleType:       user.vehicleType || '',
    licenseNumber:     user.licenseNumber || '',
    insuranceNumber:   user.insuranceNumber || '',
    bankAccountNumber: user.bankAccountNumber || '',
    bankName:          user.bankName || '',
  });
  const [err, setErr] = useState('');
  const h = { Authorization: `Bearer ${token}` };
  const isDriver = user.role === 'driver';

  const field = (key: keyof typeof form, label: string) => (
    <input key={key} type="text" style={styles.formInput}
      placeholder={label} value={form[key]}
      onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))} />
  );

  const submit = async () => {
    if (!form.name.trim()) { setErr('Name is required.'); return; }
    try {
      await axios.patch(`${API}/users/${user._id}`, form, { headers: h });
      onDone(); onClose();
    } catch (e: any) { setErr(e.response?.data?.message || 'Failed to update user.'); }
  };

  return (
    <Overlay onClose={onClose}>
      <h3 style={styles.modalTitle}>Edit — {user.name}</h3>
      {field('name', 'Name')}
      {field('phone', 'Phone')}
      {field('email', 'Email')}
      {isDriver && field('vehicleType', 'Car Type (e.g. Sedan)')}
      {isDriver && field('licenseNumber', 'License Number')}
      {isDriver && field('insuranceNumber', 'Insurance Number')}
      {field('bankAccountNumber', 'Bank Account Number')}
      {field('bankName', 'Bank Name')}
      {err && <p style={{ color: '#e53935', fontSize: 13, margin: '4px 0 8px' }}>{err}</p>}
      <div style={styles.modalActions}>
        <button style={{ ...styles.actionBtn, backgroundColor: '#9ca3af' }} onClick={onClose}>Cancel</button>
        <button style={{ ...styles.actionBtn, backgroundColor: '#1a73e8' }} onClick={submit}>Save Changes</button>
      </div>
    </Overlay>
  );
}

// ─── Funds modal ───────────────────────────────────────────────────────────────

function FundsModal({ driver, token, onClose, onDone }: {
  driver: { id: string; name: string; balance: number };
  token: string; onClose: () => void; onDone: () => void;
}) {
  const [amount, setAmount] = useState('');
  const [err, setErr] = useState('');
  const h = { Authorization: `Bearer ${token}` };

  const submit = async (sign: 1 | -1) => {
    const n = Number(amount);
    if (isNaN(n) || n <= 0) { setErr('Enter a positive number.'); return; }
    try {
      await axios.patch(`${API}/drivers/${driver.id}/add-funds`, { amount: n * sign }, { headers: h });
      onDone(); onClose();
    } catch (e: any) { setErr(e.response?.data?.message || 'Failed to update funds.'); }
  };

  return (
    <Overlay onClose={onClose}>
      <h3 style={styles.modalTitle}>Adjust Wallet — {driver.name}</h3>
      <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 14px' }}>
        Current balance: <strong>{driver.balance.toLocaleString()} SDG</strong>
      </p>
      <input type="number" style={styles.formInput} placeholder="Amount (e.g. 500)"
        value={amount} min={0} onChange={e => setAmount(e.target.value)} />
      {err && <p style={{ color: '#e53935', fontSize: 13, margin: '4px 0 8px' }}>{err}</p>}
      <div style={styles.modalActions}>
        <button style={{ ...styles.actionBtn, backgroundColor: '#9ca3af' }} onClick={onClose}>Cancel</button>
        <button style={{ ...styles.actionBtn, backgroundColor: '#b91c1c' }} onClick={() => submit(-1)}>Deduct Funds</button>
        <button style={{ ...styles.actionBtn, backgroundColor: '#15803d' }} onClick={() => submit(1)}>Add Funds</button>
      </div>
    </Overlay>
  );
}

// ─── Add admin modal ───────────────────────────────────────────────────────────

function AddAdminModal({ token, onClose, onDone }: { token: string; onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [err, setErr] = useState('');
  const h = { Authorization: `Bearer ${token}` };

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      setErr('All fields are required.'); return;
    }
    try {
      await axios.post(`${API}/create-admin`, form, { headers: h });
      onDone(); onClose();
    } catch (e: any) { setErr(e.response?.data?.message || 'Failed to create admin.'); }
  };

  return (
    <Overlay onClose={onClose}>
      <h3 style={styles.modalTitle}>Add New Admin</h3>
      {(['name', 'email', 'password'] as const).map(f => (
        <input key={f} type={f === 'password' ? 'password' : 'text'} style={styles.formInput}
          placeholder={f.charAt(0).toUpperCase() + f.slice(1)}
          value={form[f]} onChange={e => setForm(p => ({ ...p, [f]: e.target.value }))} />
      ))}
      {err && <p style={{ color: '#e53935', fontSize: 13, margin: '4px 0 8px' }}>{err}</p>}
      <div style={styles.modalActions}>
        <button style={{ ...styles.actionBtn, backgroundColor: '#9ca3af' }} onClick={onClose}>Cancel</button>
        <button style={{ ...styles.actionBtn, backgroundColor: '#1a73e8' }} onClick={submit}>Create Admin</button>
      </div>
    </Overlay>
  );
}

// ─── Contact modal ─────────────────────────────────────────────────────────────

interface ContactEntry { label: string; name: string; phone: string }

function ContactModal({ title, contacts, onClose }: { title: string; contacts: ContactEntry[]; onClose: () => void }) {
  return (
    <Overlay onClose={onClose}>
      <h3 style={styles.modalTitle}>{title}</h3>
      {contacts.map(c => (
        <div key={c.label} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{c.label}</div>
          <div style={{ fontWeight: 600, fontSize: 15, color: '#111' }}>{c.name}</div>
          <div style={{ fontSize: 15, color: '#1a73e8', marginTop: 2 }}>📞 {c.phone}</div>
        </div>
      ))}
      {contacts.length === 0 && <p style={{ color: '#9ca3af' }}>No contact info available.</p>}
      <div style={{ textAlign: 'right', marginTop: 8 }}>
        <button style={{ ...styles.actionBtn, backgroundColor: '#1a73e8' }} onClick={onClose}>Close</button>
      </div>
    </Overlay>
  );
}

// ─── Login ─────────────────────────────────────────────────────────────────────

const SLIDES = ['/khartoum-night.jpg', '/khartoum-street.jpg'];

function Login({ onLogin }: { onLogin: (token: string) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setSlide(s => (s + 1) % SLIDES.length), 4000);
    return () => clearInterval(t);
  }, []);

  const handleLogin = async () => {
    setError('');
    try {
      const res = await axios.post(`${API}/login`, { email, password });
      onLogin(res.data.token);
    } catch { setError('Invalid credentials. Please try again.'); }
  };

  const onKey = (e: React.KeyboardEvent) => { if (e.key === 'Enter') handleLogin(); };

  return (
    <div style={{ position: 'relative', height: '100vh', overflow: 'hidden', fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}>
      <style>{`
        .wr-slide {
          position: absolute; inset: 0;
          background-size: cover; background-position: center;
          transition: opacity 1.4s ease-in-out;
        }
        .wr-login-input {
          width: 100%; padding: 12px 14px; border-radius: 10px;
          border: 1.5px solid #e5e7eb; font-size: 15px;
          box-sizing: border-box; color: #111; outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
          font-family: inherit; background: #fff;
        }
        .wr-login-input:focus { border-color: #1a73e8; box-shadow: 0 0 0 3px rgba(26,115,232,0.18); }
        .wr-login-btn {
          width: 100%; padding: 13px 0;
          background: #1a73e8; color: #fff; border: none;
          border-radius: 10px; font-size: 16px; font-weight: 600;
          cursor: pointer; letter-spacing: 0.01em;
          transition: background 0.15s, transform 0.1s;
          font-family: inherit;
        }
        .wr-login-btn:hover  { background: #1557b0; }
        .wr-login-btn:active { transform: scale(0.99); }
        .wr-dot { cursor: pointer; transition: all 0.35s ease; border-radius: 4px; height: 7px; }
      `}</style>

      {/* ── Full-screen background slideshow ── */}
      {SLIDES.map((src, i) => (
        <div key={i} className="wr-slide" style={{
          backgroundImage: `url(${src})`,
          opacity: slide === i ? 1 : 0,
        }} />
      ))}

      {/* Dark overlay across entire screen */}
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.52)' }} />

      {/* ── Page layout on top of background ── */}
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center',
      }}>

        {/* Top branding */}
        <div style={{ paddingTop: 60, textAlign: 'center' }}>
          <h1 style={{
            margin: 0, fontSize: 42, fontWeight: 800, color: '#fff',
            letterSpacing: '-0.02em', textShadow: '0 2px 20px rgba(0,0,0,0.5)',
          }}>
            🚗 We Ride Sudan
          </h1>
          <p style={{
            margin: '10px 0 0', fontSize: 16, color: 'rgba(255,255,255,0.82)',
            fontWeight: 400, letterSpacing: '0.04em',
            textShadow: '0 1px 8px rgba(0,0,0,0.4)',
          }}>
            Powering Khartoum's Roads
          </p>
        </div>

        {/* Login card — centered, slightly below midpoint */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', paddingTop: 40, width: '100%', justifyContent: 'center' }}>
          <div style={{
            width: '100%', maxWidth: 420, margin: '0 16px',
            backgroundColor: '#fff', borderRadius: 20,
            boxShadow: '0 24px 64px rgba(0,0,0,0.35), 0 8px 24px rgba(0,0,0,0.2)',
            padding: '36px 36px 28px',
          }}>
            <div style={{ marginBottom: 28, textAlign: 'center' }}>
              <h2 style={{ margin: '0 0 6px', fontSize: 24, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em' }}>
                Welcome back
              </h2>
              <p style={{ margin: 0, fontSize: 14, color: '#94a3b8' }}>Sign in to the admin dashboard</p>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                Email address
              </label>
              <input className="wr-login-input" type="email" placeholder="admin@weride.com"
                value={email} onChange={e => setEmail(e.target.value)} onKeyDown={onKey} />
            </div>

            <div style={{ marginBottom: 22 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                Password
              </label>
              <input className="wr-login-input" type="password" placeholder="••••••••"
                value={password} onChange={e => setPassword(e.target.value)} onKeyDown={onKey} />
            </div>

            {error && (
              <div style={{
                marginBottom: 16, padding: '10px 14px',
                backgroundColor: '#fef2f2', borderRadius: 8,
                border: '1px solid #fecaca', color: '#b91c1c', fontSize: 13,
              }}>
                {error}
              </div>
            )}

            <button className="wr-login-btn" onClick={handleLogin}>Sign In</button>

            <p style={{ textAlign: 'center', marginTop: 22, marginBottom: 0, fontSize: 12, color: '#94a3b8' }}>
              We Ride Sudan — Admin Dashboard
            </p>
          </div>
        </div>

        {/* Slide indicator dots at bottom */}
        <div style={{ paddingBottom: 32, display: 'flex', gap: 8, alignItems: 'center' }}>
          {SLIDES.map((_, i) => (
            <div key={i} className="wr-dot" onClick={() => setSlide(i)} style={{
              width: slide === i ? 26 : 7,
              backgroundColor: slide === i ? '#fff' : 'rgba(255,255,255,0.4)',
            }} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Dashboard ─────────────────────────────────────────────────────────────────

type DriverFilter = 'all' | 'pending' | 'active' | 'suspended';

function Dashboard({ token, onLogout }: { token: string; onLogout: () => void }) {
  const [tab, setTab] = useState('rides');
  const [rides, setRides] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [dispatchLog, setDispatchLog] = useState<any[]>([]);
  const [driverFilter, setDriverFilter] = useState<DriverFilter>('all');

  const [addModalRole, setAddModalRole] = useState<UserRole | null>(null);
  const [profileUser, setProfileUser] = useState<any>(null);
  const [pendingConfirm, setPendingConfirm] = useState<{
    message: string; onConfirm: () => void; confirmLabel?: string; confirmColor?: string;
  } | null>(null);
  const [selectedRideIds, setSelectedRideIds] = useState<Set<string>>(new Set());
  const [contactModal, setContactModal] = useState<{ title: string; contacts: ContactEntry[] } | null>(null);
  const [rideDriverMap, setRideDriverMap] = useState<Record<string, string>>({});
  const [editUser, setEditUser]   = useState<any>(null);
  const [fundsUser, setFundsUser] = useState<{ id: string; name: string; balance: number } | null>(null);
  const [chartDays, setChartDays] = useState(30);
  const [chartData, setChartData] = useState<any>(null);
  const [admins, setAdmins] = useState<any[]>([]);
  const [addAdminModal, setAddAdminModal] = useState(false);

  // Decode current admin's own ID from JWT (for self-deletion guard)
  const myId = (() => {
    try { return (JSON.parse(atob(token.split('.')[1])) as any).userId as string; }
    catch { return ''; }
  })();

  const h = { Authorization: `Bearer ${token}` };

  const reloadUsers  = async () => { const r = await axios.get(`${API}/users`, { headers: h }); setUsers(r.data.users || []); };
  const reloadAdmins = async () => { try { const r = await axios.get(`${API}/admins`, { headers: h }); setAdmins(r.data.admins || []); } catch {} };
  const reloadRides = async () => { const r = await axios.get(`${API}/rides`, { headers: h }); setRides(r.data.rides || []); };
  const reloadDispatchLog = async () => {
    try { const r = await axios.get(`${API}/dispatch-log`, { headers: h }); setDispatchLog(r.data.logs || r.data || []); }
    catch { setDispatchLog([]); }
  };
  const loadCharts = async (days: number) => {
    setChartData(null);
    try {
      const r = await axios.get(`${API}/charts?days=${days}`, { headers: h });
      const d = r.data;
      // If all time-series have zero values, fall back to sample data so charts are never blank
      const hasRealData = (d.ridesPerDay || []).some((x: any) => x.count > 0) ||
                          (d.topDrivers || []).length > 0 ||
                          (d.statusBreakdown || []).length > 0;
      setChartData(hasRealData ? d : makeSampleData(days));
    } catch {
      setChartData(makeSampleData(days));
    }
  };

  useEffect(() => {
    reloadRides(); reloadUsers(); reloadDispatchLog(); reloadAdmins();
    axios.get(`${API}/stats`, { headers: h }).then(r => setStats(r.data));
    loadCharts(30);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ── Derived lists ─────────────────────────────────────────────────────────

  const driverStatus = (d: any): string => d.status || (d.isActive ? 'active' : 'suspended');

  const drivers     = users.filter(u => u.role === 'driver');
  const riders      = users.filter(u => u.role === 'rider');
  const dispatchers = users.filter(u => u.role === 'dispatcher');
  const availableDrivers = drivers.filter(d => d.isActive && (d.isAvailable || d.isOnline));
  const boardRides  = rides.filter(r => !['completed', 'cancelled'].includes(r.status));

  const filteredDrivers = driverFilter === 'all'
    ? drivers
    : drivers.filter(d => driverStatus(d) === driverFilter);

  // ── User actions ──────────────────────────────────────────────────────────

  const toggleUser = async (id: string, isActive: boolean) => {
    await axios.patch(`${API}/users/${id}`, { isActive: !isActive }, { headers: h });
    await reloadUsers();
  };

  const deleteUser = (id: string, name: string) => {
    setPendingConfirm({
      message: `Delete "${name}" permanently? This cannot be undone.`,
      onConfirm: async () => { await axios.delete(`${API}/users/${id}`, { headers: h }); await reloadUsers(); setPendingConfirm(null); },
    });
  };

  const approveDriver = async (id: string) => {
    await axios.patch(`${API}/drivers/${id}/approve`, {}, { headers: h });
    await reloadUsers();
  };

  const rejectDriver = (id: string, name: string) => {
    setPendingConfirm({
      message: `Reject "${name}"? Their account will be marked as rejected.`,
      confirmLabel: 'Reject',
      confirmColor: '#e53935',
      onConfirm: async () => {
        await axios.patch(`${API}/drivers/${id}/reject`, {}, { headers: h });
        await reloadUsers();
        setPendingConfirm(null);
      },
    });
  };

  // ── Ride selection ────────────────────────────────────────────────────────

  const toggleRide = (id: string) => {
    setSelectedRideIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  };
  const toggleAllRides = () => {
    setSelectedRideIds(prev => prev.size === rides.length ? new Set() : new Set(rides.map(r => r._id)));
  };
  const deleteSelectedRides = () => {
    const count = selectedRideIds.size;
    setPendingConfirm({
      message: `Delete ${count} selected ride${count !== 1 ? 's' : ''} permanently?`,
      onConfirm: async () => {
        await Promise.all(Array.from(selectedRideIds).map(id => axios.delete(`${API}/rides/${id}`, { headers: h })));
        await reloadRides(); setSelectedRideIds(new Set()); setPendingConfirm(null);
      },
    });
  };

  // ── Dispatch board ────────────────────────────────────────────────────────

  const assignDriver = async (rideId: string) => {
    const driverId = rideDriverMap[rideId];
    if (!driverId) return;
    try {
      await axios.patch(`${API}/rides/${rideId}/assign`, { driverId }, { headers: h });
      await reloadRides(); await reloadDispatchLog();
      setRideDriverMap(prev => { const s = { ...prev }; delete s[rideId]; return s; });
    } catch (e: any) { alert(e.response?.data?.message || 'Assignment failed.'); }
  };

  const openContact = (ride: any) => {
    const contacts: ContactEntry[] = [];
    if (ride.rider?.name) contacts.push({ label: 'Rider', name: ride.rider.name, phone: ride.rider.phone || 'N/A' });
    if (ride.driver?.name) contacts.push({ label: 'Driver', name: ride.driver.name, phone: ride.driver.phone || 'N/A' });
    setContactModal({ title: 'Contact Info', contacts });
  };

  // ── Shared row helpers ────────────────────────────────────────────────────

  const clickableRow: React.CSSProperties = { cursor: 'pointer' };
  const hoverRow   = (e: React.MouseEvent<HTMLTableRowElement>) => (e.currentTarget.style.backgroundColor = '#f8fafc');
  const unhoverRow = (e: React.MouseEvent<HTMLTableRowElement>) => (e.currentTarget.style.backgroundColor = '');

  const ghost = (variant: 'green' | 'red' | 'amber' | 'blue'): React.CSSProperties => {
    const v = {
      green: ['#f0fdf4', '#15803d', '#bbf7d0'],
      red:   ['#fff1f2', '#be123c', '#fecdd3'],
      amber: ['#fffbeb', '#b45309', '#fde68a'],
      blue:  ['#eff6ff', '#1d4ed8', '#bfdbfe'],
    }[variant];
    return { padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 500, backgroundColor: v[0], color: v[1], border: `1.5px solid ${v[2]}` };
  };

  const statusBadge = (isActive: boolean) => {
    const [bg, fg, label] = isActive ? ['#dcfce7', '#15803d', 'Active'] : ['#fee2e2', '#b91c1c', 'Suspended'];
    return <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, display: 'inline-block', backgroundColor: bg, color: fg }}>{label}</span>;
  };

  const driverStatusBadge = (d: any) => {
    const s = driverStatus(d);
    const m: Record<string, [string, string]> = {
      pending:   ['#fef3c7', '#b45309'],
      active:    ['#dcfce7', '#15803d'],
      suspended: ['#fee2e2', '#b91c1c'],
      rejected:  ['#f3f4f6', '#6b7280'],
    };
    const [bg, fg] = m[s] || ['#f3f4f6', '#6b7280'];
    return <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, display: 'inline-block', backgroundColor: bg, color: fg }}>{s.charAt(0).toUpperCase() + s.slice(1)}</span>;
  };

  const driverActionCell = (d: any) => {
    const s = driverStatus(d);
    return (
      <td onClick={e => e.stopPropagation()} style={{ padding: '10px 16px' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {s === 'pending' ? (
            <>
              <button style={ghost('green')} onClick={() => approveDriver(d._id)}>✓ Approve</button>
              <button style={ghost('red')} onClick={() => rejectDriver(d._id, d.name)}>✗ Reject</button>
              <button style={ghost('blue')} onClick={() => setEditUser(d)}>Edit</button>
            </>
          ) : (
            <>
              <button style={ghost(s === 'active' ? 'amber' : 'green')} onClick={() => toggleUser(d._id, d.isActive)}>
                {s === 'active' ? 'Suspend' : 'Activate'}
              </button>
              <button style={ghost('blue')} onClick={() => setFundsUser({ id: d._id, name: d.name, balance: (d as any).walletBalance ?? 0 })}>$ Funds</button>
              <button style={ghost('blue')} onClick={() => setEditUser(d)}>Edit</button>
              <button style={ghost('red')} onClick={() => deleteUser(d._id, d.name)}>Delete</button>
            </>
          )}
        </div>
      </td>
    );
  };

  const userActionCell = (u: any) => (
    <td onClick={e => e.stopPropagation()} style={{ padding: '10px 16px' }}>
      <div style={{ display: 'flex', gap: 6 }}>
        <button style={ghost(u.isActive ? 'amber' : 'green')} onClick={() => toggleUser(u._id, u.isActive)}>
          {u.isActive ? 'Suspend' : 'Activate'}
        </button>
        <button style={ghost('blue')} onClick={() => setEditUser(u)}>Edit</button>
        <button style={ghost('red')} onClick={() => deleteUser(u._id, u.name)}>Delete</button>
      </div>
    </td>
  );

  const tabs = ['rides','drivers','riders','dispatchers','dispatch-board','dispatch-log','stats','ai','live-map','settings','admins'];
  const tabLabel: Record<string, string> = {
    rides: '🚗 Rides', drivers: '🧑 Drivers', riders: '👤 Riders',
    dispatchers: '👨‍💼 Dispatchers', 'dispatch-board': '📋 Dispatch Board', 'dispatch-log': '📝 Activity Log',
    stats: '📊 Statistics', ai: '🤖 AI Assistant', 'live-map': '🗺 Live Map',
    settings: '⚙️ Settings', admins: '👥 Admins',
  };

  const pendingCount = drivers.filter(d => driverStatus(d) === 'pending').length;

  return (
    <div style={styles.container}>
      <style>{`
        .wr-table-scroll { overflow-x: auto; }
        .wr-table { min-width: 100%; width: max-content; border-collapse: collapse; }
        .wr-table thead tr { background: #f8fafc; }
        .wr-table thead th {
          padding: 10px 16px; text-align: left; font-size: 11px; font-weight: 600;
          color: #374151; text-transform: uppercase; letter-spacing: 0.06em;
          white-space: nowrap; border-bottom: 1px solid #e2e8f0; border-right: 1px solid #e2e8f0;
        }
        .wr-table thead th:last-child { border-right: none; }
        .wr-table tbody tr { border-bottom: 1px solid #f1f5f9; }
        .wr-table tbody tr:last-child { border-bottom: none; }
        .wr-table tbody td {
          padding: 12px 16px; font-size: 14px; color: #111111; vertical-align: middle;
          border-right: 1px solid #f1f5f9;
        }
        .wr-table tbody td:last-child { border-right: none; }
        .wr-table tbody tr:hover { background: #f8fafc !important; }
      `}</style>

      {/* ── Sidebar ── */}
      <div style={styles.sidebar}>
        <h2 style={styles.sidebarTitle}>🚗 WeRide Admin</h2>
        {tabs.map(t => (
          <div key={t} style={{ ...styles.navItem, ...(tab === t ? styles.navActive : {}), position: 'relative' }} onClick={() => setTab(t)}>
            {tabLabel[t]}
            {t === 'drivers' && pendingCount > 0 && (
              <span style={styles.navBadge}>{pendingCount}</span>
            )}
          </div>
        ))}
        <div style={{ marginTop: 'auto' }}>
          <div style={styles.navItem} onClick={onLogout}>🚪 Logout</div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div style={styles.main}>

        {/* STATS */}
        {tab === 'stats' && (
          <div>
            <h2 style={{ margin: '0 0 20px', fontSize: 20, fontWeight: 700, color: '#0f172a' }}>📊 Overview</h2>

            {/* Summary cards — unchanged */}
            {stats && (
              <div style={styles.statsGrid}>
                {[
                  ['Total Rides', stats.totalRides], ['Active Rides', stats.activeRides],
                  ['Total Drivers', stats.totalDrivers], ['Total Riders', stats.totalRiders],
                  ['Total Revenue', `${stats.totalRevenue} SDG`],
                ].map(([label, value]) => (
                  <div key={label as string} style={styles.statCard}>
                    <div style={{ fontSize: 32, fontWeight: 'bold' }}>{value}</div>
                    <div>{label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Date range filter */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '36px 0 20px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Analytics</h3>
                {chartData?.isSample && (
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: '#94a3b8' }}>Showing sample data — real data will appear as rides accumulate</p>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {([7, 30, 90] as const).map(d => (
                  <button key={d} onClick={() => { setChartDays(d); loadCharts(d); }} style={{
                    padding: '7px 18px', borderRadius: 20, border: 'none', cursor: 'pointer',
                    fontSize: 12, fontWeight: chartDays === d ? 700 : 400,
                    backgroundColor: chartDays === d ? '#0f172a' : '#f1f5f9',
                    color: chartDays === d ? '#fff' : '#64748b',
                    transition: 'all 0.15s',
                  }}>Last {d} days</button>
                ))}
              </div>
            </div>

            {/* Charts grid */}
            {chartData ? (() => {
              const ridesData   = fillDates(chartData.ridesPerDay   || [], chartDays, 'count');
              const revenueData = fillDates(chartData.revenuePerDay || [], chartDays, 'revenue');
              const signupData  = fillDates(chartData.riderSignups  || [], chartDays, 'count');
              const pieData     = (chartData.statusBreakdown || []).map((d: any) => ({ name: d._id, value: d.count }));
              const driverData  = chartData.topDrivers || [];
              const tickInterval = chartDays <= 7 ? 0 : chartDays <= 30 ? 4 : 13;

              // Aggregate daily data into weekly buckets for bar charts when days > 14
              const bucketData = (raw: { date: string; [k: string]: any }[], vk: string) => {
                if (chartDays <= 14) return raw.map((r, i) => ({ ...r, label: shortDate(r.date), _i: i }));
                const weeks: { label: string; [k: string]: any }[] = [];
                for (let i = 0; i < raw.length; i += 7) {
                  const slice = raw.slice(i, i + 7);
                  const total = slice.reduce((s, r) => s + (r[vk] || 0), 0);
                  weeks.push({ label: shortDate(slice[0].date), [vk]: total, _i: i });
                }
                return weeks;
              };

              const ridesBuckets   = bucketData(ridesData, 'count');
              const revenueBuckets = bucketData(revenueData, 'revenue');

              return (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 22 }}>

                  {/* 1 — Rides per day / week (bar, multi-color) */}
                  <ChartCard
                    title="Total Rides"
                    subtitle={chartDays <= 14 ? `Daily for last ${chartDays} days` : `Weekly totals for last ${chartDays} days`}
                  >
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={ridesBuckets} margin={{ top: 20, right: 10, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} width={28} axisLine={false} tickLine={false} />
                        <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                        <Bar dataKey="count" name="Rides" radius={[6, 6, 0, 0]} maxBarSize={48}>
                          <LabelList dataKey="count" position="top" style={{ fontSize: 10, fontWeight: 700, fill: '#475569' }} />
                          {ridesBuckets.map((_: any, i: number) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>

                  {/* 2 — Revenue per day / week (bar, multi-color) */}
                  <ChartCard
                    title="Revenue (SDG)"
                    subtitle={chartDays <= 14 ? `Daily revenue for last ${chartDays} days` : `Weekly revenue for last ${chartDays} days`}
                  >
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={revenueBuckets} margin={{ top: 20, right: 10, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} width={48} axisLine={false} tickLine={false} />
                        <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} formatter={(v: any) => [`${Number(v).toLocaleString()} SDG`, 'Revenue']} />
                        <Bar dataKey="revenue" name="Revenue" radius={[6, 6, 0, 0]} maxBarSize={48}>
                          <LabelList dataKey="revenue" position="top" style={{ fontSize: 9, fontWeight: 700, fill: '#475569' }}
                            formatter={(v: any) => Number(v) >= 1000 ? `${Math.round(Number(v)/1000)}k` : String(v)} />
                          {revenueBuckets.map((_: any, i: number) => <Cell key={i} fill={PALETTE[(i + 2) % PALETTE.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>

                  {/* 3 — Rides trend line (full width) */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <ChartCard title="Ride Volume Trend" subtitle={`Daily rides over the last ${chartDays} days`}>
                      <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={ridesData} margin={{ top: 10, right: 16, bottom: 0, left: 0 }}>
                          <defs>
                            <linearGradient id="rideGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#4361ee" stopOpacity={0.18} />
                              <stop offset="95%" stopColor="#4361ee" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                          <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={shortDate}
                            interval={tickInterval} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} width={28} axisLine={false} tickLine={false} />
                          <Tooltip labelFormatter={(v: any) => shortDate(String(v))} />
                          <Line type="monotone" dataKey="count" stroke="#4361ee" strokeWidth={2.5}
                            dot={{ r: 3, fill: '#4361ee', strokeWidth: 0 }} name="Rides" />
                        </LineChart>
                      </ResponsiveContainer>
                    </ChartCard>
                  </div>

                  {/* 4 — Ride status breakdown (pie) */}
                  <ChartCard title="Ride Status Breakdown" subtitle={`Distribution of ${pieData.reduce((s: number, d: any) => s + d.value, 0)} rides`}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <ResponsiveContainer width="60%" height={280}>
                        <PieChart>
                          <Pie data={pieData} cx="50%" cy="50%" outerRadius={105} innerRadius={45}
                            dataKey="value" paddingAngle={3}>
                            {pieData.map((_: any, i: number) => (
                              <Cell key={i} fill={Object.values(STATUS_COLORS)[i] ?? PALETTE[i % PALETTE.length]} />
                            ))}
                            <LabelList dataKey="value" position="inside" style={{ fontSize: 11, fontWeight: 700, fill: '#fff' }} />
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {pieData.map((d: any, i: number) => (
                          <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ width: 10, height: 10, borderRadius: 3, flexShrink: 0,
                              backgroundColor: Object.values(STATUS_COLORS)[i] ?? PALETTE[i % PALETTE.length] }} />
                            <span style={{ fontSize: 12, color: '#475569', textTransform: 'capitalize', flex: 1 }}>{d.name.replace('_', ' ')}</span>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{d.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </ChartCard>

                  {/* 5 — Top 5 drivers (horizontal bars, multi-color) */}
                  <ChartCard title="Top 5 Drivers" subtitle="Most completed rides in period">
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={driverData} layout="vertical" margin={{ top: 4, right: 48, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} axisLine={false} tickLine={false} />
                        <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11, fill: '#334155' }} axisLine={false} tickLine={false} />
                        <Tooltip />
                        <Bar dataKey="count" name="Rides" radius={[0, 6, 6, 0]} maxBarSize={32}>
                          <LabelList dataKey="count" position="right" style={{ fontSize: 11, fontWeight: 700, fill: '#475569' }}
                            formatter={(v: any) => `${v} rides`} />
                          {driverData.map((_: any, i: number) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>

                  {/* 6 — Rider signups trend (full width) */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <ChartCard title="New Rider Signups" subtitle={`Daily new riders over the last ${chartDays} days`}>
                      <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={signupData} margin={{ top: 10, right: 16, bottom: 0, left: 0 }}>
                          <defs>
                            <linearGradient id="signupGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2} />
                              <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                          <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={shortDate}
                            interval={tickInterval} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} width={28} axisLine={false} tickLine={false} />
                          <Tooltip labelFormatter={(v: any) => shortDate(String(v))} />
                          <Line type="monotone" dataKey="count" stroke="#f59e0b" strokeWidth={2.5}
                            dot={{ r: 3, fill: '#f59e0b', strokeWidth: 0 }} name="New Riders" />
                        </LineChart>
                      </ResponsiveContainer>
                    </ChartCard>
                  </div>

                </div>
              );
            })() : (
              <div style={{ padding: 64, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
                Loading charts…
              </div>
            )}
          </div>
        )}

        {/* RIDES */}
        {tab === 'rides' && (
          <div>
            <div style={styles.tabHeader}>
              <h2 style={{ margin: 0 }}>🚗 All Rides</h2>
              {selectedRideIds.size > 0 && (
                <button style={{ ...styles.actionBtn, backgroundColor: '#e53935' }} onClick={deleteSelectedRides}>
                  Delete Selected ({selectedRideIds.size})
                </button>
              )}
            </div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: 36, textAlign: 'center' }}>
                    <input type="checkbox" checked={rides.length > 0 && selectedRideIds.size === rides.length} onChange={toggleAllRides} />
                  </th>
                  <th>Rider</th><th>Driver</th><th>Pickup</th><th>Dropoff</th>
                  <th>Fare</th><th>Status</th><th>Ratings</th><th>Requested At</th>
                </tr>
              </thead>
              <tbody>
                {rides.map(r => (
                  <tr key={r._id}>
                    <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedRideIds.has(r._id)} onChange={() => toggleRide(r._id)} />
                    </td>
                    <td>{r.rider?.name || 'N/A'}</td>
                    <td>{r.driver?.name || 'Unassigned'}</td>
                    <td>{r.pickup?.address || '–'}</td>
                    <td>{r.destination?.address || '–'}</td>
                    <td>{r.fare?.estimated} SDG</td>
                    <td>
                      <span style={{ ...styles.badge, backgroundColor: r.status === 'completed' ? '#4caf50' : r.status === 'cancelled' ? '#f44336' : '#ff9800' }}>
                        {r.status}
                      </span>
                    </td>
                    <td style={{ fontSize: 13, lineHeight: '1.6' }}>
                      {r.status === 'completed'
                        ? <><div>🚗 {starRating(r.driverRating ?? r.rating)}</div><div>👤 {starRating(r.riderRating)}</div></>
                        : '—'}
                    </td>
                    <td style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{formatTs(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* DRIVERS */}
        {tab === 'drivers' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' }}>Drivers</h2>
                <p style={{ margin: '3px 0 0', fontSize: 13, color: '#94a3b8' }}>
                  {filteredDrivers.length} {driverFilter === 'all' ? 'total' : driverFilter}
                </p>
              </div>
              <button style={styles.addBtn} onClick={() => setAddModalRole('driver')}>+ Add Driver</button>
            </div>

            {/* Filter tabs */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
              {(['all', 'pending', 'active', 'suspended'] as DriverFilter[]).map(f => {
                const count = f === 'all' ? drivers.length : drivers.filter(d => driverStatus(d) === f).length;
                const isFilterActive = driverFilter === f;
                const [activeBg, activeColor] = (
                  f === 'pending'   ? ['#fef3c7', '#b45309'] :
                  f === 'active'    ? ['#dcfce7', '#15803d'] :
                  f === 'suspended' ? ['#fee2e2', '#b91c1c'] :
                                      ['#0f172a', '#fff']
                ) as [string, string];
                return (
                  <button key={f} onClick={() => setDriverFilter(f)} style={{
                    padding: '6px 16px', borderRadius: 20, border: 'none', cursor: 'pointer',
                    fontSize: 13, fontWeight: isFilterActive ? 600 : 400,
                    backgroundColor: isFilterActive ? activeBg : '#f1f5f9',
                    color: isFilterActive ? activeColor : '#64748b',
                  }}>
                    {f.charAt(0).toUpperCase() + f.slice(1)} ({count})
                  </button>
                );
              })}
            </div>

            <div style={styles.tableCard}>
              <div className="wr-table-scroll">
              <table className="wr-table">
                <thead>
                  <tr>
                    <th style={{ width: 52 }}></th>
                    <th>Name</th><th>Phone</th><th>Online</th><th>Status</th>
                    <th>Rating</th><th>Wallet</th><th>Unpaid</th><th>Lock</th>
                    <th>Car Type</th><th>License No.</th><th>Insurance</th>
                    <th>Signed Up</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDrivers.map(d => (
                    <tr key={d._id} style={clickableRow} onMouseEnter={hoverRow} onMouseLeave={unhoverRow} onClick={() => setProfileUser(d)}>
                      <td style={{ padding: '10px 12px' }}><AvatarCell name={d.name} photoUrl={(d as any).profilePhoto} /></td>
                      <td style={{ fontWeight: 600, color: '#111111' }}>{d.name}</td>
                      <td style={{ color: '#111111' }}>{d.phone}</td>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 500, color: d.isOnline ? '#16a34a' : '#111111' }}>
                          <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: d.isOnline ? '#22c55e' : '#cbd5e1', display: 'inline-block' }} />
                          {d.isOnline ? 'Online' : 'Offline'}
                        </span>
                      </td>
                      <td>{driverStatusBadge(d)}</td>
                      <td style={{ color: '#111111' }}>{starRating(d.rating)}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600, color: (d as any).walletBalance < 0 ? '#b91c1c' : '#15803d' }}>
                        {((d as any).walletBalance ?? 0).toLocaleString()} SDG
                      </td>
                      <td style={{ textAlign: 'center', color: '#111111' }}>{(d as any).unpaidRideCount ?? 0}</td>
                      <td onClick={e => e.stopPropagation()}>
                        {(d as any).isLocked
                          ? <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, backgroundColor: '#fee2e2', color: '#b91c1c' }}>🔒 Locked</span>
                          : <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, backgroundColor: '#f0fdf4', color: '#15803d' }}>✓ OK</span>}
                      </td>
                      <td style={{ color: '#111111' }}>{(d as any).vehicleType || <span style={{ color: '#94a3b8' }}>N/A</span>}</td>
                      <td style={{ fontFamily: 'monospace', color: '#111111' }}>{(d as any).licenseNumber || <span style={{ color: '#94a3b8' }}>N/A</span>}</td>
                      <td style={{ fontFamily: 'monospace', color: '#111111' }}>{(d as any).insuranceNumber || <span style={{ color: '#94a3b8' }}>N/A</span>}</td>
                      <td style={{ color: '#111111', whiteSpace: 'nowrap' }}>{formatTs(d.createdAt)}</td>
                      {driverActionCell(d)}
                    </tr>
                  ))}
                  {filteredDrivers.length === 0 && (
                    <tr><td colSpan={14} style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 14 }}>
                      No {driverFilter === 'all' ? '' : driverFilter + ' '}drivers found.
                    </td></tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        )}

        {/* RIDERS */}
        {tab === 'riders' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' }}>Riders</h2>
                <p style={{ margin: '3px 0 0', fontSize: 13, color: '#94a3b8' }}>{riders.length} total</p>
              </div>
              <button style={styles.addBtn} onClick={() => setAddModalRole('rider')}>+ Add Rider</button>
            </div>
            <div style={styles.tableCard}>
              <div className="wr-table-scroll">
              <table className="wr-table">
                <thead>
                  <tr>
                    <th style={{ width: 52 }}></th>
                    <th>Name</th><th>Phone</th><th>Email</th><th>Account</th>
                    <th>Total Rides</th><th>Rating</th><th>Signed Up</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {riders.map(r => (
                    <tr key={r._id} style={clickableRow} onMouseEnter={hoverRow} onMouseLeave={unhoverRow} onClick={() => setProfileUser(r)}>
                      <td style={{ padding: '10px 12px' }}><AvatarCell name={r.name} photoUrl={(r as any).profilePhoto} /></td>
                      <td style={{ fontWeight: 600, color: '#111111' }}>{r.name}</td>
                      <td style={{ color: '#111111' }}>{r.phone}</td>
                      <td style={{ color: '#111111' }}>{r.email || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                      <td>{statusBadge(r.isActive)}</td>
                      <td style={{ color: '#111111' }}>{r.totalRides || 0}</td>
                      <td style={{ color: '#111111' }}>{starRating(r.rating)}</td>
                      <td style={{ color: '#111111', whiteSpace: 'nowrap' }}>{formatTs(r.createdAt)}</td>
                      {userActionCell(r)}
                    </tr>
                  ))}
                  {riders.length === 0 && (
                    <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 14 }}>No riders yet.</td></tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        )}

        {/* DISPATCHERS */}
        {tab === 'dispatchers' && (
          <div>
            <div style={styles.tabHeader}>
              <h2 style={{ margin: 0 }}>👨‍💼 Dispatchers</h2>
              <button style={{ ...styles.actionBtn, backgroundColor: '#1a73e8' }} onClick={() => setAddModalRole('dispatcher')}>
                + Add Dispatcher
              </button>
            </div>
            <table style={styles.table}>
              <thead><tr><th>Name</th><th>Phone</th><th>Email</th><th>Account</th><th>Actions</th></tr></thead>
              <tbody>
                {dispatchers.map(d => (
                  <tr key={d._id} style={clickableRow} onMouseEnter={hoverRow} onMouseLeave={unhoverRow} onClick={() => setProfileUser(d)}>
                    <td>{d.name}</td><td>{d.phone}</td><td>{d.email || '–'}</td>
                    <td>{statusBadge(d.isActive)}</td>
                    {userActionCell(d)}
                  </tr>
                ))}
                {dispatchers.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>No dispatchers yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* DISPATCH BOARD */}
        {tab === 'dispatch-board' && (
          <div>
            <div style={styles.tabHeader}>
              <h2 style={{ margin: 0 }}>📋 Dispatch Board</h2>
              <span style={{ fontSize: 13, color: '#9ca3af' }}>
                {boardRides.length} active ride{boardRides.length !== 1 ? 's' : ''} · {availableDrivers.length} drivers available
              </span>
            </div>
            <table style={styles.table}>
              <thead><tr><th>Rider</th><th>Route</th><th>Status</th><th>Driver</th><th>Assign</th><th>Contact</th></tr></thead>
              <tbody>
                {boardRides.map(r => {
                  const needsDriver = !r.driver || r.status === 'pending' || r.status === 'searching';
                  return (
                    <tr key={r._id}>
                      <td>{r.rider?.name || 'N/A'}</td>
                      <td style={{ fontSize: 12, color: '#6b7280', maxWidth: 200 }}>
                        {r.pickup?.address || '–'} → {r.destination?.address || r.dropoff?.address || '–'}
                      </td>
                      <td><span style={{ ...styles.badge, backgroundColor: needsDriver ? '#ff9800' : '#3b82f6' }}>{r.status}</span></td>
                      <td>
                        {needsDriver ? (
                          <select style={styles.select} value={rideDriverMap[r._id] || ''}
                            onChange={e => setRideDriverMap(prev => ({ ...prev, [r._id]: e.target.value }))}>
                            <option value="">Select driver…</option>
                            {availableDrivers.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                          </select>
                        ) : <span style={{ fontWeight: 500 }}>{r.driver?.name}</span>}
                      </td>
                      <td>
                        {needsDriver && (
                          <button style={{ ...styles.actionBtn, backgroundColor: rideDriverMap[r._id] ? '#43a047' : '#d1d5db', fontSize: 12 }}
                            disabled={!rideDriverMap[r._id]} onClick={() => assignDriver(r._id)}>Assign</button>
                        )}
                      </td>
                      <td>
                        <button style={{ ...styles.actionBtn, backgroundColor: '#6366f1', fontSize: 12 }} onClick={() => openContact(r)}>Contact</button>
                      </td>
                    </tr>
                  );
                })}
                {boardRides.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>No active rides.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* DISPATCH LOG */}
        {tab === 'dispatch-log' && (
          <div>
            <div style={styles.tabHeader}>
              <h2 style={{ margin: 0 }}>📝 Dispatch Activity Log</h2>
              <button style={{ ...styles.actionBtn, backgroundColor: '#6366f1' }} onClick={reloadDispatchLog}>Refresh</button>
            </div>
            <table style={styles.table}>
              <thead><tr><th>Dispatcher</th><th>Ride ID</th><th>Driver Assigned</th><th>Time</th></tr></thead>
              <tbody>
                {dispatchLog.map((entry, i) => (
                  <tr key={entry._id || i}>
                    <td>{entry.dispatcher?.name || entry.dispatcherName || '–'}</td>
                    <td style={{ fontSize: 12, color: '#6b7280', fontFamily: 'monospace' }}>{entry.rideId || entry.ride?._id || entry.ride || '–'}</td>
                    <td>{entry.driver?.name || entry.driverName || '–'}</td>
                    <td style={{ fontSize: 13, color: '#6b7280' }}>{formatTs(entry.createdAt)}</td>
                  </tr>
                ))}
                {dispatchLog.length === 0 && (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>No activity yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'ai'       && <AIAssistant token={token} />}
        {tab === 'live-map' && <LiveMap />}
        {tab === 'settings' && <Settings token={token} />}

        {/* ADMINS */}
        {tab === 'admins' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' }}>Admins</h2>
                <p style={{ margin: '3px 0 0', fontSize: 13, color: '#94a3b8' }}>{admins.length} admin account{admins.length !== 1 ? 's' : ''}</p>
              </div>
              <button style={styles.addBtn} onClick={() => setAddAdminModal(true)}>+ Add Admin</button>
            </div>
            <div style={styles.tableCard}>
              <div className="wr-table-scroll">
              <table className="wr-table">
                <thead>
                  <tr>
                    <th>Name</th><th>Email</th><th>Created</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map(a => {
                    const isSelf = String(a._id) === myId;
                    return (
                      <tr key={a._id}>
                        <td style={{ fontWeight: 600, color: '#111111' }}>{a.name}</td>
                        <td style={{ color: '#111111', fontFamily: 'monospace', fontSize: 13 }}>{a.email}</td>
                        <td style={{ color: '#111111', whiteSpace: 'nowrap' }}>{formatTs(a.createdAt)}</td>
                        <td onClick={e => e.stopPropagation()} style={{ padding: '10px 16px' }}>
                          {isSelf ? (
                            <span title="You cannot delete your own account" style={{
                              padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 500,
                              backgroundColor: '#f1f5f9', color: '#94a3b8', cursor: 'not-allowed',
                              border: '1.5px solid #e2e8f0', display: 'inline-block',
                            }}>🔒 Current user</span>
                          ) : (
                            <button style={{
                              padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 500,
                              backgroundColor: '#fff1f2', color: '#be123c', border: '1.5px solid #fecdd3',
                            }} onClick={() => setPendingConfirm({
                              message: `Permanently delete admin "${a.name}" (${a.email})? This cannot be undone.`,
                              confirmLabel: 'Delete Admin',
                              confirmColor: '#be123c',
                              onConfirm: async () => {
                                await axios.delete(`${API}/admins/${a._id}`, { headers: h });
                                await reloadAdmins();
                                setPendingConfirm(null);
                              },
                            })}>Delete</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {admins.length === 0 && (
                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 14 }}>No admins found.</td></tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      {addModalRole   && <AddUserModal role={addModalRole} token={token} onClose={() => setAddModalRole(null)} onDone={reloadUsers} />}
      {addAdminModal  && <AddAdminModal token={token} onClose={() => setAddAdminModal(false)} onDone={reloadAdmins} />}
      {editUser       && <EditUserModal user={editUser} token={token} onClose={() => setEditUser(null)} onDone={reloadUsers} />}
      {fundsUser    && <FundsModal driver={fundsUser} token={token} onClose={() => setFundsUser(null)} onDone={reloadUsers} />}
      {profileUser  && <ProfileModal user={profileUser} onClose={() => setProfileUser(null)} />}
      {contactModal && <ContactModal title={contactModal.title} contacts={contactModal.contacts} onClose={() => setContactModal(null)} />}
      {pendingConfirm && (
        <ConfirmDialog
          message={pendingConfirm.message}
          confirmLabel={pendingConfirm.confirmLabel}
          confirmColor={pendingConfirm.confirmColor}
          onConfirm={pendingConfirm.onConfirm}
          onCancel={() => setPendingConfirm(null)}
        />
      )}
    </div>
  );
}

// ─── App root ──────────────────────────────────────────────────────────────────

export default function App() {
  const [token, setToken] = useState(localStorage.getItem('adminToken') || '');
  const handleLogin  = (t: string) => { localStorage.setItem('adminToken', t); setToken(t); };
  const handleLogout = ()          => { localStorage.removeItem('adminToken'); setToken(''); };
  return token ? <Dashboard token={token} onLogout={handleLogout} /> : <Login onLogin={handleLogin} />;
}

// ─── Styles ────────────────────────────────────────────────────────────────────

const styles: any = {
  loginContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#1a1a2e' },
  loginBox:       { backgroundColor: '#fff', padding: 40, borderRadius: 12, width: 380, textAlign: 'center' as const },
  logo:           { fontSize: 24, marginBottom: 8 },
  loginTitle:     { color: '#333', marginBottom: 24 },
  input:          { width: '100%', padding: 12, marginBottom: 12, borderRadius: 8, border: '1px solid #ddd', fontSize: 15, boxSizing: 'border-box' as const },
  button:         { width: '100%', padding: 14, backgroundColor: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, fontSize: 16, cursor: 'pointer' },
  error:          { color: 'red', marginBottom: 12 },
  container:      { display: 'flex', height: '100vh', fontFamily: 'sans-serif' },
  sidebar:        { width: 220, backgroundColor: '#1a1a2e', color: '#fff', padding: 20, display: 'flex', flexDirection: 'column' as const, overflowY: 'auto' as const },
  sidebarTitle:   { fontSize: 16, marginBottom: 24 },
  navItem:        { padding: '12px 16px', cursor: 'pointer', borderRadius: 8, marginBottom: 4, fontSize: 14 },
  navActive:      { backgroundColor: '#1a73e8' },
  navBadge:       { position: 'absolute' as const, top: 6, right: 8, background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 10, padding: '1px 5px', minWidth: 16, textAlign: 'center' as const },
  main:           { flex: 1, padding: 30, backgroundColor: '#f5f5f5', overflowY: 'auto' as const },
  statsGrid:      { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginTop: 20 },
  statCard:       { backgroundColor: '#fff', padding: 24, borderRadius: 12, textAlign: 'center' as const, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  table:          { width: '100%', borderCollapse: 'collapse' as const, backgroundColor: '#fff', borderRadius: 12, overflow: 'hidden' },
  tableCard:      { backgroundColor: '#fff', borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04)', overflow: 'hidden' },
  chartCard:      { backgroundColor: '#fff', borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04)', padding: '20px 24px 16px' },
  chartTitle:     { margin: '0 0 16px', fontSize: 13, fontWeight: 600, color: '#0f172a', letterSpacing: '-0.01em' },
  addBtn:         { padding: '9px 18px', backgroundColor: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', letterSpacing: '0.01em' },
  badge:          { padding: '3px 10px', borderRadius: 20, color: '#fff', fontSize: 12, display: 'inline-block' },
  actionBtn:      { padding: '6px 14px', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', fontSize: 13 },
  tabHeader:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  select:         { padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, backgroundColor: '#fff', cursor: 'pointer', minWidth: 150 },
  overlay:        { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalBox:       { background: '#fff', borderRadius: 14, padding: 28, width: 440, maxWidth: '90vw', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' },
  modalTitle:     { marginTop: 0, marginBottom: 18, color: '#111', fontSize: 18 },
  modalActions:   { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 },
  formInput:      { display: 'block', width: '100%', padding: '10px 12px', marginBottom: 10, borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' as const },
};
